/* eslint-disable camelcase */
import i18n from 'i18next';
import { all, call, put, takeLatest } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';
import { IntegrationModel } from '../../../types/apiResponseTypes';

import { backendErrorNotification, frontendNotification } from '../../../components/Notification';
import { NotificationTypes } from '../../../components/Notification/notificationEnums';
import HttpStatus from '../../../enums/httpStatus';
import { UrlPaths, UrlPathsSuper } from '../../../enums/urlPaths.enum';
import history from '../../../services/history';
import {
  getIntegrationModelsApi,
  refreshReq,
  signinApi,
  signinByTokenReq,
} from '../../../services/requests';
import { AuthResponseData } from '../../../types/auth';
import * as actions from './actions';

export function* signIn({ payload }: ActionType<typeof actions.signInRequest>) {
  try {
    const { login, password } = payload;

    const { response, error } = yield call(signinApi, login, password);

    if (response && response.status === HttpStatus.OK) {
      const loginDataSuccessful: AuthResponseData = response.data.data;
      yield put(actions.signInSuccess(loginDataSuccessful));
    } else {
      throw error;
    }
  } catch (error: any) {
    if (error.response) {
      const { response } = error;

      if (response.status === HttpStatus.PERMISSION_DENIED) {
        frontendNotification({
          message: i18n.t('invalidData'),
          type: NotificationTypes.WARNING,
        });
      } else {
        backendErrorNotification(error);
      }
    } else {
      frontendNotification({
        message: i18n.t('unavailableService'),
        type: NotificationTypes.ERROR,
      });
    }

    yield put(actions.signInFailure());
  }
}

export function* signInSuccess({ payload }: ActionType<typeof actions.signInSuccess>) {
  if (!payload.user.is_superadmin) {
    const { data } = yield getIntegrationModelsApi.index();
    const integrationModels: IntegrationModel[] = data.data;

    yield put(actions.setIntegrationModels(integrationModels));
    history.push(UrlPaths.ROOT);
  } else {
    history.push(UrlPathsSuper.HOME_SALES);
  }
}

export function* signInByToken({ payload }: ActionType<typeof actions.signInByTokenRequest>) {
  try {
    const { token, urlPath } = payload;

    const { response, error } = yield call(signinByTokenReq, token);

    if (response && response.status === HttpStatus.OK) {
      const loginDataSuccessful: AuthResponseData = response.data.data;

      yield put(actions.signInSuccess(loginDataSuccessful));

      const { data } = yield getIntegrationModelsApi.index();
      const integrationModels: IntegrationModel[] = data.data;

      yield put(actions.setIntegrationModels(integrationModels));

      history.push(urlPath);
    } else {
      throw error;
    }
  } catch (error: any) {
    if (error.response) {
      const { response } = error;

      if (response.status === HttpStatus.PERMISSION_DENIED) {
        frontendNotification({
          message: i18n.t('invalidData'),
          type: NotificationTypes.WARNING,
        });
      } else {
        backendErrorNotification(error);
      }
    } else {
      frontendNotification({
        message: i18n.t('unavailableService'),
        type: NotificationTypes.ERROR,
      });
    }

    yield put(actions.signInFailure());
  }
}

export function* refreshToken({ payload }: ActionType<typeof actions.refreshRequest>) {
  try {
    const { accessToken } = payload;
    const { response, error } = yield call(refreshReq, accessToken);

    if (response && response.status === HttpStatus.OK) {
      const { data } = response;
      yield put(actions.refreshSuccess({ newToken: data.token }));
    } else if (response && response.status === HttpStatus.UNAUTHORIZED) {
      throw error;
    }
  } catch (error) {
    frontendNotification({
      message: 'sessao expirada',
      type: NotificationTypes.WARNING,
    });
    yield put(actions.refreshFailure());
    yield put(actions.logoutRequest());
  }
}

export function* logOut() {
  try {
    yield put(actions.logoutSuccess());

    sessionStorage.clear();

    history.push(UrlPaths.ROOT);
  } catch (err) {
    yield put(actions.signInFailure());
  }
}

const authSaga = all([
  takeLatest('@auth/SIGN_IN_REQUEST', signIn),
  takeLatest('@auth/SIGN_IN_SUCCESS', signInSuccess),
  takeLatest('@auth/SIGN_IN_BY_TOKEN_REQUEST', signInByToken),
  takeLatest('@auth/LOGOUT_REQUEST', logOut),
]);

export default authSaga;
